import { useGetGuidParam } from '@risksmart-app/components/src/routes/routes.utils';
import { Parent_Type_Enum } from '@risksmart-app/web-graphql-client/generated/graphql';
import { type FC, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ModalForm } from 'src/components/form/form/ModalForm';
import { useGetIssueUpdateById } from 'src/hooks/queries/issue-update/useGetIssueUpdateById';
import type { ObjectWithContributors } from 'src/rbac/Permission';
import { useHasPermissionQuery } from 'src/rbac/useHasPermission';
import type { IssueUpdatesFields } from 'src/schemas/issueUpdates';
import { defaultValues, IssueUpdatesSchema } from 'src/schemas/issueUpdates';

import { useFileUpdate } from '@/hooks/mutations/file/useFileUpdate';
import {
  useInsertIssueUpdate,
  useUpdateIssueUpdate,
} from '@/hooks/mutations/issue-update';
import { saveFilesForOptionalCreatedEntity } from '@/utils/createWithFiles';
import { issueDetailsUrl } from '@/utils/urls';

import IssueUpdateForm from './forms/IssueUpdateForm';

type Props = {
  onDismiss: () => void;
  issueUpdateId?: string;
  parent: ObjectWithContributors;
};

const IssueUpdateModal: FC<Props> = ({ onDismiss, issueUpdateId, parent }) => {
  const issueId = useGetGuidParam('issueId');

  const { updateFiles } = useFileUpdate();
  const { insertIssueUpdate } = useInsertIssueUpdate();
  const { updateIssueUpdate } = useUpdateIssueUpdate();

  const {
    hasPermission: canCreateIssueUpdate,
    loading: canCreateIssueUpdateLoading,
  } = useHasPermissionQuery('insert:issue_update', parent);
  const {
    hasPermission: canEditIssueUpdate,
    loading: canEditIssueUpdateLoading,
  } = useHasPermissionQuery('update:issue_update', parent);
  const { t } = useTranslation('common');
  // Resume file persistence against the successful insert instead of creating
  // another issue update when the user retries a partial save.
  const createdIssueUpdateIdRef = useRef<string | undefined>(undefined);
  const { data, loading } = useGetIssueUpdateById({
    queryArgs: { id: issueUpdateId ?? '' },
    shouldSkip: !issueUpdateId,
  });

  const issueUpdate = data?.issue_update[0];
  const canModify = issueUpdate
    ? canEditIssueUpdate && !canEditIssueUpdateLoading
    : canCreateIssueUpdate && !canCreateIssueUpdateLoading;
  const onSave = async (data: IssueUpdatesFields) => {
    const { files } = data;
    let savedIssueUpdateId = issueUpdate?.Id ?? createdIssueUpdateIdRef.current;
    let createdEntity =
      !issueUpdate && savedIssueUpdateId
        ? {
            id: savedIssueUpdateId,
            destination: issueDetailsUrl(issueId),
          }
        : undefined;
    if (issueUpdate) {
      const result = await updateIssueUpdate({
        ...data,
        CustomAttributeData: data.CustomAttributeData,
        ParentIssueId: issueId,
        Id: issueUpdate.Id,
        OriginalTimestamp: issueUpdate.ModifiedAtTimestamp,
      });
      if (result.update_issue_update?.affected_rows !== 1) {
        throw new Error(
          'Records not updated. Record may have been updated by another user'
        );
      }
    } else if (!savedIssueUpdateId) {
      const result = await insertIssueUpdate({
        ...data,
        CustomAttributeData: data.CustomAttributeData,
        ParentIssueId: issueId,
      });
      savedIssueUpdateId = result.insert_issue_update_one?.Id;
      if (savedIssueUpdateId) {
        createdIssueUpdateIdRef.current = savedIssueUpdateId;
        createdEntity = {
          id: savedIssueUpdateId,
          destination: issueDetailsUrl(issueId),
        };
      }
    }

    if (!savedIssueUpdateId) {
      throw new Error('Missing result');
    }

    const saveFiles = () =>
      updateFiles({
        parentType: Parent_Type_Enum.IssueUpdate,
        parentId: savedIssueUpdateId,
        originalFiles: issueUpdate?.files.map((rf) => rf.file),
        selectedFiles: files,
      });
    await saveFilesForOptionalCreatedEntity({ createdEntity, saveFiles });

    return { savedIds: [savedIssueUpdateId], createdEntity };
  };

  if (loading) {
    return null;
  }

  return (
    <ModalForm<IssueUpdatesFields>
      i18n={t('actionUpdates')}
      values={
        issueUpdate
          ? {
              ...defaultValues,
              ...issueUpdate,
              files:
                issueUpdate?.files.map((f) => f.file).filter(Boolean) ?? [],
            }
          : undefined
      }
      defaultValues={defaultValues}
      schema={IssueUpdatesSchema}
      onSave={onSave}
      onDismiss={onDismiss}
      formId={'issue-update-form'}
      visible={true}
      readOnly={!canModify || loading}
      parentType={Parent_Type_Enum.IssueUpdate}
    >
      <IssueUpdateForm readOnly={!canModify || loading} />
    </ModalForm>
  );
};

export default IssueUpdateModal;
